import { read, utils, WorkBook } from 'xlsx';
import removeAccents from 'remove-accents';
import { DeviceFault } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultForm';
import { IFormInputSpec, IFormInputType } from 'pages/revisions-module/template-editor/editors/form/types';
import {
  MeasurementRowLockMetadata,
  resetMeasurementRowLockMetadata,
} from 'components/forms/revision/fill-out/modals/device-measurement-modal/measurement-row-lock';

const FAULTS_COLUMN_LABEL = 'Nedostatky';
const FAULTS_COLUMN_NAME = '_faults';

interface MeasurementImportColumn {
  name: string;
  label: string;
  type: IFormInputType | typeof FAULTS_COLUMN_NAME;
  spec?: IFormInputSpec;
}

export type ImportedMeasurementRow<TMeasurement> = TMeasurement & {
  _id: string;
  _faults: DeviceFault[];
} & MeasurementRowLockMetadata;

export interface ParseMeasurementWorkbookParams {
  workbook: WorkBook;
  spec: Readonly<IFormInputSpec[]>;
  createRowId: () => string;
}

/**
 * Reads XLSX bytes into a workbook without depending on browser or component state.
 */
export function readMeasurementWorkbook(data: ArrayBuffer): WorkBook {
  return read(data, { type: 'array' });
}

/**
 * Validates and converts formatted XLSX cell text according to its form-editor specification.
 */
export function validateAndConvertMeasurementImportValue(
  value: unknown,
  spec: IFormInputSpec,
  dataRowIndex: number
): unknown {
  if (value === undefined || value === null || String(value) === '') {
    return value;
  }

  switch (spec.type) {
    case 'text':
      return value;

    case 'number': {
      const normalizedValue = String(value).trim().replace(',', '.');
      const numberValue = Number(normalizedValue);

      if (normalizedValue === '' || !Number.isFinite(numberValue)) {
        throw new Error(`Riadok ${dataRowIndex}, stĺpec "${spec.label}": "${value}" nie je platné číslo.`);
      }

      if (spec.min !== undefined && numberValue < spec.min) {
        throw new Error(
          `Riadok ${dataRowIndex}, stĺpec "${spec.label}": hodnota ${numberValue} je menšia ako povolené minimum ${spec.min}.`
        );
      }

      if (typeof spec.max === 'number' && numberValue > spec.max) {
        throw new Error(
          `Riadok ${dataRowIndex}, stĺpec "${spec.label}": hodnota ${numberValue} je väčšia ako povolené maximum ${spec.max}.`
        );
      }

      return spec.precision !== undefined ? Number(numberValue.toFixed(spec.precision)) : numberValue;
    }

    case 'date': {
      const dateValue = String(value).trim();
      const isoMatch = dateValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      const dotMatch = dateValue.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      const slashMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      let year: number | undefined;
      let month: number | undefined;
      let day: number | undefined;

      if (isoMatch) {
        year = Number(isoMatch[1]);
        month = Number(isoMatch[2]);
        day = Number(isoMatch[3]);
      } else if (dotMatch) {
        day = Number(dotMatch[1]);
        month = Number(dotMatch[2]);
        year = Number(dotMatch[3]);
      } else if (slashMatch) {
        month = Number(slashMatch[1]);
        day = Number(slashMatch[2]);
        year = Number(slashMatch[3]);
      }

      if (year === undefined || month === undefined || day === undefined) {
        throw new Error(
          `Riadok ${dataRowIndex}, stĺpec "${spec.label}": "${value}" nie je platný dátum. Podporované formáty: yyyy-mm-dd, dd.mm.yyyy, mm/dd/yyyy.`
        );
      }

      const date = new Date(year, month - 1, day);

      if (
        Number.isNaN(date.getTime()) ||
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        throw new Error(`Riadok ${dataRowIndex}, stĺpec "${spec.label}": "${value}" nie je platný dátum.`);
      }

      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    case 'checkbox': {
      const normalizedValue = removeAccents(String(value)).toLowerCase();

      return normalizedValue === 'ano' || normalizedValue === 'true';
    }

    case 'select': {
      const normalizedValue = String(value).trim();

      if (!spec.options.includes(normalizedValue)) {
        throw new Error(
          `Riadok ${dataRowIndex}, stĺpec "${
            spec.label
          }": "${normalizedValue}" nie je platná možnosť. Povolené hodnoty: ${spec.options.join(', ')}.`
        );
      }

      return normalizedValue;
    }

    default:
      return value;
  }
}

/**
 * Returns formatted cell text, falling back to the raw value for in-memory workbooks.
 */
function getCellImportValue(
  cell: { t?: string; v?: unknown; w?: string } | undefined,
  type: MeasurementImportColumn['type']
): unknown {
  if ((type === 'number' && cell?.t === 'n') || (type === 'checkbox' && cell?.t === 'b')) {
    return cell.v;
  }

  if (cell?.w !== undefined) {
    return cell.w;
  }

  return cell?.v === undefined || cell.v === null ? undefined : String(cell.v);
}

/**
 * Parses JSON cell text and returns a fallback when it is malformed.
 */
function parseJsonSafe<T>(value: string | undefined, fallback: T): T {
  try {
    return JSON.parse(value ?? '') as T;
  } catch {
    return fallback;
  }
}

/**
 * Parses the first worksheet into fresh, unlocked measurement rows.
 */
export function parseMeasurementWorkbook<TMeasurement>({
  workbook,
  spec,
  createRowId,
}: ParseMeasurementWorkbookParams): ImportedMeasurementRow<TMeasurement>[] {
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const columns: Array<MeasurementImportColumn & { address: string }> = [
    ...spec.map((columnSpec, index) => ({
      address: utils.encode_col(index),
      name: columnSpec.name,
      label: columnSpec.label,
      type: columnSpec.type,
      spec: columnSpec,
    })),
    {
      address: utils.encode_col(spec.length),
      name: FAULTS_COLUMN_NAME,
      label: FAULTS_COLUMN_LABEL,
      type: FAULTS_COLUMN_NAME,
    },
  ];

  for (const { address, label } of columns) {
    const actualLabel = String(sheet?.[`${address}1`]?.v ?? '')
      .replace('(povinné)', '')
      .trim();

    if (actualLabel !== label) {
      throw new Error(`Skontrolujte si názvy stĺpcov v súbore. Očakáva sa názov "${label}" v stĺpci ${address}`);
    }
  }

  const rows: ImportedMeasurementRow<TMeasurement>[] = [];
  const errors: string[] = [];

  for (let worksheetRow = 2; columns.some(({ address }) => sheet?.[`${address}${worksheetRow}`]); worksheetRow++) {
    const row = resetMeasurementRowLockMetadata({
      _faults: [] as DeviceFault[],
      _id: createRowId(),
    }) as ImportedMeasurementRow<TMeasurement>;

    for (const column of columns) {
      const value = getCellImportValue(sheet?.[`${column.address}${worksheetRow}`], column.type);

      if (column.type === FAULTS_COLUMN_NAME) {
        row._faults = parseJsonSafe<DeviceFault[]>(value === undefined ? undefined : String(value), []);
        continue;
      }

      try {
        (row as Record<string, unknown>)[column.name] = validateAndConvertMeasurementImportValue(
          value,
          column.spec!,
          worksheetRow - 1
        );
      } catch (error) {
        errors.push(error instanceof Error ? error.message : String(error));
      }
    }

    rows.push(row);
  }

  if (errors.length > 0) {
    throw new Error(errors.join('\n'));
  }

  return rows;
}
